import {
  Badge,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { useUserPresenceStatus } from "hooks/useConversations";
import {
  ConversationDto,
  UserPresenceResponseDtoStatusEnum,
} from "libs/cellera-sdk";
import { useMemo } from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";
import { Patient } from "types";
import { formatPatientName } from "utils/patient";

import BadgeAvatar from "./BadgeAvatars";

interface ConversationItemProps extends ConversationDto {
  isSelected: boolean;
  onSelect: (conversation: ConversationDto) => void;
}

const ConversationItem: React.FC<ConversationItemProps> = (
  props: ConversationItemProps
) => {
  const {
    isSelected,
    onSelect,
    patient,
    lastMessageAt,
    lastMessagePreview,
    unreadCountProvider,
  } = props;

  const { colors } = useCelleraTheme();

  const { data: userPresenceStatus } = useUserPresenceStatus(patient.patientId);

  const patientName = useMemo(() => {
    const patientName = formatPatientName(patient as Patient).trim();
    return patientName || "N/A";
  }, [patient]);

  const lastMessageTime = useMemo(() => {
    if (!lastMessageAt) return "";
    return format(lastMessageAt, "hh:mm a");
  }, [lastMessageAt]);

  const lastMessageDate = useMemo(() => {
    if (!lastMessageAt) return "";
    return format(lastMessageAt, "MM/dd/yyyy");
  }, [lastMessageAt]);

  return (
    <ListItem
      disablePadding
      sx={{
        borderBottom: `1px solid ${colors.border.light}`,
      }}
    >
      <ListItemButton
        className="rounded-2xl h-18 gap-3"
        sx={{
          backgroundColor: isSelected
            ? colors.background.grayLight
            : "transparent",
        }}
        onClick={() => onSelect(props)}
      >
        <BadgeAvatar
          photoUrl={patient.photoUrl}
          presenceStatus={
            userPresenceStatus?.status ??
            UserPresenceResponseDtoStatusEnum.Offline
          }
        />

        <ListItemText
          primary={
            <Typography
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                variant="body1"
                component="span"
                sx={{
                  fontWeight: 500,
                  fontSize: "14px",
                  color: colors.text.primary,
                }}
              >
                {patientName}
              </Typography>
              <Typography
                variant="body1"
                component="span"
                sx={{
                  fontWeight: 400,
                  fontSize: "12px",
                  color: colors.text.primary,
                  display: { md: "none", lg: "block" },
                }}
              >
                {lastMessageTime}
              </Typography>
            </Typography>
          }
          secondary={
            <Typography
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: { xs: 0, md: 1 },
                flexWrap: { xs: "wrap", md: "nowrap" },
              }}
            >
              <Typography
                variant="body2"
                component="span"
                sx={{
                  fontWeight: 500,
                  fontSize: "12px",
                  color: colors.text.secondary,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {lastMessagePreview}
              </Typography>
              <Typography
                variant="body1"
                component="span"
                sx={{
                  fontWeight: 400,
                  fontSize: "12px",
                  color: colors.text.primary,
                }}
              >
                {lastMessageDate}
              </Typography>
            </Typography>
          }
        />

        <Badge variant="dot" color="primary" invisible={!unreadCountProvider} />
      </ListItemButton>
    </ListItem>
  );
};

export default ConversationItem;
